import DayPicker from "@JohnLewisPartnership/jl-design-system/dist/components/day-picker/DayPicker";
import { TextInput } from "@JohnLewisPartnership/jl-design-system/dist/components/text-input/TextInput";
import EMAIL from "@JohnLewisPartnership/checkout-forms/dist/form/fields/email";
import FIRST_NAME from "@JohnLewisPartnership/checkout-forms/dist/form/fields/firstName";
import LAST_NAME from "@JohnLewisPartnership/checkout-forms/dist/form/fields/lastName";
import ORDER_ID from "@JohnLewisPartnership/checkout-forms/dist/form/fields/orderId";
import { POSTCODE_UK } from "@JohnLewisPartnership/checkout-forms/dist/form/fields/postcode";
import TITLE from "@JohnLewisPartnership/checkout-forms/dist/form/fields/title";
import React from "react";
import displayConditions, { ELEMENTS, shouldShowPurchasedOnline } from "../../utils";
import Header from "../header/Header";
import ImageUpload from "../image-upload/image-upload";
import ShopOrOnline from "../shop-or-online/ShopOrOnline";
import styles from "./styles.module.scss";
import { last7Dates } from "./utils";

import {
  JLProductCodeDescription,
  LowerPricePhotosDescription,
  OnlinePurchasePhoneDescription,
  OrderNumberDescription,
  ProductColorDescription,
  ShopPurchasePhoneDescription,
  YourReceiptDescription,
} from "./descriptions";

const last7DaysMemoised = last7Dates();

const createCustomInput = (handleFormState, responses) => props => {
  const {
    id,
    component = TextInput,
    type = "text",
    className = `fieldLarge`,
    label,
    name,
    required = false,
    ...restProps
  } = props;
  const handleChange = e => handleFormState(id, e.target.value);
  const inputProps = {
    onBlur: () => {},
    onChange: handleChange,
    onFocus: () => {},
    name: name || id,
    value: responses[id],
  };
  return {
    id,
    name: id,
    key: id,
    className: className,
    component: component,
    label,
    type,
    required,
    input: { ...inputProps },
    onChange: handleChange,
    ...restProps,
  };
};

const receiptUploadElement = (attachmentsHandlers, attributes) => {
  const { id, className = "fieldLarge", label, description, attachmentsInfo = {}, ...restProps } = attributes;
  const attachments = attachmentsInfo.attachments || [];
  const props = {
    name: id,
    label: label,
    id: id,
    key: id,
    describedById: id,
    description: description,
    className: className,
    required: true,
    type: "file",
    maxUploads: 3,
    attachments,
    onAttachmentAdded: attachmentsHandlers.addAttachment,
    onAttachmentRemoved: attachmentsHandlers.removeAttachment,
    extensionProps: { accept: `image/*,image/heic` },
    supportedMediaTypePrefix: "image",
    validationMessageKeys: {
      required: "Please add at least one photo",
    },
    attachmentsLength: attachments.length,
    setAttachmentError: attachmentsHandlers.setAttachmentError,
    fileMeta: { error: attachmentsInfo.attachmentError, touched: !!attachmentsInfo.attachmentError },
    ...restProps,
  };
  return {
    component: () => <ImageUpload {...props} />,
    ...props,
  };
};

export const formElements = ({ addAttachment, removeAttachment, setAttachmentError, responses, handleChange }) => {
  const shouldDisplay = displayConditions(responses);
  const customInputElementBuilder = createCustomInput(handleChange, responses);
  const isItemPurchasedOnline = shouldShowPurchasedOnline(responses);
  let elements = [
    {
      id: ELEMENTS.ORIGINAL_PURCHASE_SECTION,
      required: false,
      name: ELEMENTS.ORIGINAL_PURCHASE_SECTION,
      component: () => <ShopOrOnline responses={responses} handleChange={handleChange} />,
    },
    {
      ...customInputElementBuilder({
        component: DayPicker,
        id: ELEMENTS.PURCHASED_DATE,
        key: `${ELEMENTS.PURCHASED_DATE}-${responses[ELEMENTS.PURCHASED_FROM]}`,
        name: ELEMENTS.PURCHASED_DATE,
        label: "When did you buy the item from us?",
        description: "We only price match within 7 days of your order date, not your delivery date.",
        value: responses[ELEMENTS.PURCHASED_DATE],
        required: true,
        dates: last7DaysMemoised,
        daysToShowPerPage: 14,
        selectedPage: 0,
        selectedDate: responses[ELEMENTS.PURCHASED_DATE],
        validationMessageKeys: {
          required: "Please select a date",
        },
        input: {
          onBlur: () => {},
          onChange: value => handleChange([ELEMENTS.PURCHASED_DATE], value),
          onFocus: () => {},
          name: ELEMENTS.PURCHASED_DATE,
          value: responses[ELEMENTS.PURCHASED_DATE],
        },
        onChange: value => handleChange([ELEMENTS.PURCHASED_DATE], value),
      }),
    },
    {
      ...receiptUploadElement(
        { addAttachment, removeAttachment, setAttachmentError },
        {
          id: ELEMENTS.RECEIPT_PHOTOS,
          label: "Your receipt",
          description: React.createElement(YourReceiptDescription),
          attachmentsInfo: responses[ELEMENTS.RECEIPT_PHOTOS],
        },
      ),
    },
    {
      ...customInputElementBuilder({
        ...ORDER_ID,
        id: ELEMENTS.ORDER_NUMBER,
        required: true,
        minLength: 9,
        maxLength: 9,
        placeholder: "e.g. 456789123",
        description: React.createElement(OrderNumberDescription),
        regex: /^[4-5][0-9]{8}$/,
        validationMessageKeys: {
          required: "Please enter your order number",
          regex: "Please enter a 9 digit order number beginning with 4 or 5",
          maxLength: "The order number should be 9 digits",
          minLength: "The order number should be 9 digits",
        },
      }),
    },
    {
      ...customInputElementBuilder({
        id: ELEMENTS.JL_PRODUCT_CODE,
        label: "John Lewis product code",
        description: React.createElement(JLProductCodeDescription, responses),
        required: true,
        placeholder: "e.g. 77203500",
        maxLength: 8,
        minLength: 8,
        regex: /^[0-9]{8,8}$/,
        validationMessageKeys: {
          required: "Please enter your product code",
          maxLength: "The product code you've entered is too long",
          minLength: "The product code should be 8 digits",
          regex: "Please enter correct product code eg. 77203500",
        },
      }),
    },
    {
      ...customInputElementBuilder({
        id: ELEMENTS.PRODUCT_NAME,
        label: "Full product name",
        description: "Including size and model",
        required: true,
        maxLength: 255,
        validationMessageKeys: {
          required: "Please enter a product name",
          maxLength: "The product name you've entered is too long",
        },
      }),
    },
    {
      ...customInputElementBuilder({
        id: ELEMENTS.PRODUCT_COLOR,
        maxLength: 20,
        placeholder: "e.g. red, white/multi",
        label: "Product colour (optional)",
        description: React.createElement(ProductColorDescription, responses),
        validationMessageKeys: {
          maxLength: "The product color you've entered is too long",
        },
      }),
    },
    {
      ...customInputElementBuilder({
        id: ELEMENTS.PRICE_PAID_AT_JL,
        label: "Original price you paid at John Lewis",
        placeholder: "0.00",
        required: true,
        regex: /^[0-9]{1,6}\.[0-9][0-9]$/,
        validationMessageKeys: {
          required: "Please enter a price",
          regex: "Please enter a price with pence. For example, 15.99 or 20.00.",
        },
      }),
    },
    {
      ...customInputElementBuilder({
        id: ELEMENTS.PRICE_TO_MATCH,
        label: "Price you'd like to match",
        placeholder: "0.00",
        required: true,
        regex: /^[0-9]{1,6}\.[0-9][0-9]$/,
        validationMessageKeys: {
          required: "Please enter a price",
          regex: "Please enter a price with pence. For example, 15.99 or 20.00.",
        },
      }),
    },
    {
      component: () => (
        <div className={styles.headerSection}>
          <Header title={"Lower price"}>Lower price</Header>
          <p>Must be from the last 7 days.</p>
        </div>
      ),
      className: styles.headerSection,
      type: "html",
      id: ELEMENTS.LOWER_PRICE_HEADER,
      key: ELEMENTS.LOWER_PRICE_HEADER,
    },
    {
      ...receiptUploadElement(
        { addAttachment, removeAttachment, setAttachmentError },
        {
          id: ELEMENTS.LOWER_PRICE_PHOTOS,
          attachmentsInfo: responses[ELEMENTS.LOWER_PRICE_PHOTOS],
          label: "Photo or screenshot of lower price",
          description: React.createElement(LowerPricePhotosDescription),
        },
      ),
    },
    {
      ...customInputElementBuilder({
        id: ELEMENTS.LINK_TO_LOWER_PRICES,
        label: "Link to lower-price item (optional)",
        description: "A direct link to the product itself and not the homepage.",
        placeholder: "www.retailer.com/product",
        required: false,
        maxLength: 255,
        //eslint-disable-next-line
        regex: /((https?:\/\/|ftp:\/\/|www\.)\S+\.[^()\n ]+((?:\([^)]*\))|[^.,;:?!"'\n\)\]<* ])+)$/i,
        validationMessageKeys: {
          regex: "Please enter a valid url",
        },
      }),
    },
    {
      component: () => (
        <div className={styles.headerSectionYourDetails}>
          <Header title={"Your details"}>Your details</Header>
        </div>
      ),
      className: styles.headerSection,
      type: "html",
      id: ELEMENTS.YOUR_DETAILS_HEADER,
      key: ELEMENTS.YOUR_DETAILS_HEADER,
    },
    {
      ...customInputElementBuilder({
        ...TITLE,
        id: ELEMENTS.USER_TITLE,
        name: ELEMENTS.USER_TITLE,
        label: "Title",
        required: true,
        maxLength: 15,
        className: "fieldLarge",
        placeholder: "Enter text",
        validationMessageKeys: {
          ...TITLE.validationMessageKeys,
          required: "Please enter your title",
        },
      }),
    },
    {
      ...customInputElementBuilder({
        ...FIRST_NAME,
        id: ELEMENTS.FIRST_NAME,
        name: ELEMENTS.FIRST_NAME,
        label: "First name",
        required: true,
        className: "fieldMedium",
        maxLength: 30,
        placeholder: "Enter text",
        validationMessageKeys: {
          ...FIRST_NAME.validationMessageKeys,
          required: "Please enter your first name",
        },
      }),
    },
    {
      ...customInputElementBuilder({
        ...LAST_NAME,
        id: ELEMENTS.LAST_NAME,
        name: ELEMENTS.LAST_NAME,
        label: "Last name",
        className: "fieldMedium",
        required: true,
        maxLength: 30,
        placeholder: "Enter text",
        validationMessageKeys: {
          ...LAST_NAME.validationMessageKeys,
          required: "Please enter your last name",
        },
      }),
    },
    {
      ...customInputElementBuilder({
        regex: /^(?:\+44\s?7\d{3}|\(?07\d{3}\)?)[\s-]?\d{3}[\s-]?\d{3}$/,
        id: ELEMENTS.PHONE,
        name: ELEMENTS.PHONE,
        label: "Phone number",
        required: true,
        maxLength: 20,
        className: "fieldLarge",
        placeholder: "e.g. 077711223344",
        description: isItemPurchasedOnline
          ? React.createElement(OnlinePurchasePhoneDescription)
          : React.createElement(ShopPurchasePhoneDescription),
        validationMessageKeys: {
          required: "Please enter a valid phone number",
          regex: "Please enter a valid phone number",
        },
      }),
    },
    {
      ...customInputElementBuilder({
        ...EMAIL,
        id: ELEMENTS.EMAIL,
        name: ELEMENTS.EMAIL,
        label: "Email address",
        required: true,
        description:
          "We’ll use this email address to let you know if your request is successful and when you should receive the refund.",
        placeholder: "e.g. yourname@gmail.com",
      }),
    },
    {
      ...customInputElementBuilder({
        ...POSTCODE_UK,
        id: ELEMENTS.POSTCODE,
        label: "Postcode (optional)",
        placeholder: "Enter text",
        required: false,
        className: "fieldLarge",
        regex: /^[A-Z]{1,2}[0-9][0-9A-Z]?\s?[0-9][A-Z]{2}$/gi,
        validationMessageKeys: {
          regex: "Please enter a valid postcode",
        },
      }),
    },
  ];
  return elements
    .map(element => {
      if (shouldDisplay(element.id)) {
        return element;
      }
      return null;
    })
    .filter(e => e);
};
